import { Button, ButtonVariant } from "components/Form/Button";
import { Input } from "components/Form/Input";
import { Layout } from "components/Layout";
import { memo, useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppRoutes, SignupScreens } from "types";
import { ApiError, apiSignupLogin } from "lib/api";
import { ReactComponent as Ai } from "assets/ai.svg";
import { ReactComponent as Connection } from "assets/connection.svg";
import * as jose from "jose";
import { Field } from "components/Form/Field";
import { toast } from "react-toastify";
import { InputPassword } from "components/Form/InputPassword";
import { useSearchParamsPassphrase } from "hooks/useSearchParamsPassphrase";
import { Storage, StorageKey } from "lib/storage";

type FormValue = {
  email: string;
  password: string;
};

export const Email = memo(function Login(props: {
  setStep: (step: SignupScreens) => void;
  setUserEmail: (email: string) => void;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const password = useSearchParamsPassphrase();
  const [processing, setProcessing] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    setError,
  } = useForm<FormValue>({
    defaultValues: {
      email: "",
      password,
    },
  });

  const onSubmit = useCallback<SubmitHandler<FormValue>>(
    async ({ email, password }) => {
      if (isValid) {
        setProcessing(true);
        try {
          const token = await apiSignupLogin(email, password);

          if (token) {
            try {
              const payload = jose.decodeJwt(token);
              Storage.set(StorageKey.TOKEN, token);
              // Store email for later use in verification/password steps
              props.setUserEmail(email);
              if (!payload.registered) {
                props.setStep(SignupScreens.Information);
              } else {
                navigate(AppRoutes.home, { replace: true });
              }
            } catch (err) {}
          }
        } catch (err) {
          if (err instanceof ApiError) {
            if (err.attr) {
              setError(err.attr as "email" | "password", {
                type: "custom",
                message: err.message,
              });
            } else {
              toast.error("Unable to login, please try again later.");
            }
          }
        } finally {
          setProcessing(false);
        }
      }
    },
    [isValid, navigate, props, setError]
  );

  const handleGoToLogin = useCallback(() => {
    // Clear any signup token before going to login
    Storage.remove(StorageKey.TOKEN);
    navigate(
      AppRoutes.login +
        (searchParams.toString() ? `?${searchParams.toString()}` : "")
    );
  }, [navigate, searchParams]);

  return (
    <form className="contents" onSubmit={handleSubmit(onSubmit)}>
      <Layout
        className="h-full text-center"
        toolbar={
          <>
            <Button disabled={!isValid || processing} type="submit">
              {processing ? "Loading ..." : "Get Started"}
            </Button>
            <Button
              onClick={handleGoToLogin}
              variant={ButtonVariant.FLAT}
              type="button"
            >
              Access an existing ContributorID
            </Button>
          </>
        }
      >
        <div className="grid h-full space-y-12">
          <div className="grid self-start justify-items-center">
            <Ai />

            <h1 className="text-24 leading-1.2 font-semibold mt-4">
              Training data collection
            </h1>

            <p className="text-858494 mt-3">
              Please enter your email and the campaign passphrase provided to
              you to begin creating your ContributorID.
            </p>
          </div>

          <div className="text-left space-y-8">
            <Field
              label="Please enter your email"
              description={errors.email?.message}
              descriptionClassName={
                errors.password?.message ? "text-fd380f" : undefined
              }
              as="label"
            >
              <Input
                disabled={processing}
                type="email"
                id="email"
                autoCapitalize="off"
                autoComplete="email"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleSubmit(onSubmit)}
                {...register("email", {
                  required: true,
                  pattern: /^\S+@\S+\.\S+$/,
                })}
              />
            </Field>

            <Field
              label="Enter campaign passphrase"
              description={errors.password?.message}
              descriptionClassName={
                errors.password?.message ? "text-fd380f" : undefined
              }
              as="label"
            >
              <InputPassword
                disabled={processing}
                id="password"
                autoCapitalize="off"
                autoComplete="password"
                {...register("password", { required: true })}
              />
            </Field>

            <ul className="space-y-3 [&_li]:flex [&_li]:gap-3 [&_svg]:flex-none [&_p]:text-14 [&_p]:text-858494">
              <li>
                <Connection />

                <p>
                  We use your email to make sure we can easily associate any
                  collected data in multiple sessions for proper algorithm
                  training and we can process any data deletion requests.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </Layout>
    </form>
  );
});
